import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useStockTypes } from "@/contexts/StockTypesContext";
import { X, Plus, Save, Trash2, Lock } from "lucide-react";

/**
 * Admin-managed Stock Types. Built-in types (roll/sheet/block/box/drum/tub)
 * can only have their display label edited. Custom types can also have their
 * default_unit changed and can be deleted (when not in use).
 */
const ManageStockTypesDialog = ({ open, onClose }) => {
  const { reload } = useStockTypes();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/stock-types");
      setRows(data || []);
    } catch (_) {
      toast.error("Could not load stock types");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const create = async () => {
    const label = newLabel.trim();
    if (!label) return toast.error("Label required");
    setBusy(true);
    try {
      await api.post("/stock-types", {
        label,
        default_unit: newUnit.trim() || "unit",
      });
      setNewLabel("");
      setNewUnit("");
      await load();
      await reload();
      toast.success(`Added type "${label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.put(`/stock-types/${row.id}`, {
        label: row.label,
        default_unit: row.default_unit,
      });
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete type "${row.label}"? Items using it must be reassigned first.`)) return;
    setBusy(true);
    try {
      await api.delete(`/stock-types/${row.id}`);
      await load();
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-types-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(720px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Inventory</div>
            <h3 className="font-display text-2xl">Manage stock types</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="manage-types-close"
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`type-row-${row.name}`}
              className="border-2 border-black rounded-xl p-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3"
            >
              <label className="flex-1">
                <div className="label-eyebrow mb-1 flex items-center gap-1">
                  Label
                  {row.builtin && (
                    <Lock size={11} strokeWidth={2.5} className="text-[#607087]" />
                  )}
                </div>
                <input
                  data-testid={`type-label-${row.name}`}
                  className="nb-input"
                  value={row.label}
                  onChange={(e) => update(row.id, { label: e.target.value })}
                />
              </label>
              <label className="w-full sm:w-32">
                <div className="label-eyebrow mb-1">Default unit</div>
                <input
                  data-testid={`type-unit-${row.name}`}
                  className="nb-input"
                  value={row.default_unit || ""}
                  disabled={row.builtin}
                  onChange={(e) => update(row.id, { default_unit: e.target.value })}
                  placeholder="m / sheet / unit"
                />
              </label>
              <div className="label-eyebrow text-[#607087] w-full sm:w-24 font-mono-num">
                {row.name}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`type-save-${row.name}`}
                  onClick={() => save(row)}
                  disabled={busy}
                  className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
                >
                  <Save size={14} strokeWidth={2.5} /> Save
                </button>
                {!row.builtin && (
                  <button
                    data-testid={`type-delete-${row.name}`}
                    onClick={() => remove(row)}
                    disabled={busy}
                    className="nb-btn nb-btn-danger p-2"
                    aria-label="delete"
                  >
                    <Trash2 size={14} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add new */}
          <div className="border-2 border-dashed border-[#001740] rounded-xl p-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3 bg-[#F4F8FB]">
            <label className="flex-1">
              <div className="label-eyebrow mb-1">New type label</div>
              <input
                data-testid="new-type-label"
                className="nb-input"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Bag, Bottle, Pail"
              />
            </label>
            <label className="w-full sm:w-32">
              <div className="label-eyebrow mb-1">Default unit</div>
              <input
                data-testid="new-type-unit"
                className="nb-input"
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                placeholder="unit"
              />
            </label>
            <button
              data-testid="new-type-add"
              onClick={create}
              disabled={busy || !newLabel.trim()}
              className="nb-btn nb-btn-success px-4 py-2 text-sm flex items-center gap-1"
            >
              <Plus size={14} strokeWidth={2.5} /> Add
            </button>
          </div>

          <div className="label-eyebrow text-[#607087] mt-1">
            Built-in types (roll, sheet, block, box, drum, tub) can be renamed but
            not removed. Custom types can be deleted only when no items use them.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageStockTypesDialog;
